import { Injectable, inject, DestroyRef, signal, computed } from '@angular/core';
import { FormBuilder, FormControl, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, forkJoin, switchMap, take } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
	Source,
	ConnectionType,
	HuntStop,
	Provider,
	HlrCurrencies,
	ProviderField,
	SourcePayload,
	ForceRerouteKeepingMccmncMode,
	ForceOption,
} from '@helpers/types/models';
import { ProvidersService } from '@helpers/repo/providers.service';
import { CurrenciesService } from '../../settings/currencies/currencies.service';
import { LocalAddressesService } from '@helpers/repo/local-addresses.service';
import { AdditionalValidators, NetworkValidators } from '@helpers/shared/validators';
import { ProviderFieldGroup } from '@helpers/types/models';
import { AlarisLanguageService, TableEntityField } from '@campaign-portal/components-library';
import { FilterType } from '@campaign-portal/namespace/common/rpc.params';
import { InputComplexType, InputType } from '@campaign-portal/namespace/common/entityField';
import { SettingsService } from '../../settings/settings/settings.service';
import { SourcesService } from '../sources.service';

export type HuntStopOption = { key: HuntStop | null, label: string }

@Injectable({ providedIn: 'root' })
export class SourceFormService {
	private readonly fb = inject(FormBuilder);
	readonly providersService = inject(ProvidersService);
	private readonly currenciesService = inject(CurrenciesService);
	private readonly localAddresses = inject(LocalAddressesService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly lService = inject(AlarisLanguageService);
	private readonly sourcesService = inject(SourcesService);
	readonly settings = inject(SettingsService);
	private readonly translate = inject(TranslateService);

	readonly errors: { key: string; value: string }[] = [
		{ key: 'mask', value: 'gl.errors.invalid_format' },
		{ key: 'required', value: 'gl.errors.required' },
	];

	readonly huntstopMap: Record<HuntStop, string> = {
		DISABLED: 'sources.huntstop.disabled',
		ON_TIMEOUT: 'sources.huntstop.on_source_timeout',
		ON_FAILED_RESPONSE: 'sources.huntstop.on_failed_response',
		UNCONDITIONAL: 'sources.huntstop.unconditional'
	};
	readonly mnpHuntstopMap: Record<Extract<HuntStop, 'DISABLED' | 'UNCONDITIONAL'>, string> = {
		DISABLED: 'sources.huntstop.disabled',
		UNCONDITIONAL: 'sources.huntstop.unconditional'
	};

	private readonly forceRerouteMap: Record<ForceRerouteKeepingMccmncMode, string> = {
		DISABLED: 'sources.fields.force_reroute_keeping_mccmnc_mode_options.disabled',
		UNCONDITIONAL: 'sources.fields.force_reroute_keeping_mccmnc_mode_options.unconditional',
		TERMINATE_ON_PORTED_NUMBER: 'sources.fields.force_reroute_keeping_mccmnc_mode_options.terminate_on_ported_number',
	};

	defaultRequestTimeout = 1000;

	private readonly languageSignal = toSignal(
		this.translate.onLangChange.pipe(
			startWith({ lang: this.translate.currentLang }),
			map(event => event.lang)
		),
		{ initialValue: this.translate.currentLang }
	);

	readonly sourceForm = this.fb.group({
		sourceId: new FormControl<number | null>(null),
		currencyName: new FormControl<HlrCurrencies | null>(null, Validators.required),
		name: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
		type: new FormControl<Provider | null>(null, Validators.required),
		connectionType: new FormControl<ConnectionType>(ConnectionType.HTTP,
			{ nonNullable: true, validators: Validators.required }),
		ttl: new FormControl<number | null>(0, Validators.pattern('^\\d+$')),
		url: new FormControl<string>('',
			{ nonNullable: true, validators: Validators.pattern(AdditionalValidators.urlPattern) }),
		statusRequestUrl: new FormControl<string>('',
			{ nonNullable: true, validators: Validators.pattern(AdditionalValidators.urlPattern) }),
		huntStop: new FormControl<HuntStopOption>({
			key: HuntStop.DISABLED,
			label: this.huntstopMap[HuntStop.DISABLED]
		}, { nonNullable: true }),
		fieldsAll: new FormControl<boolean | null>(null),
		username: new FormControl<string>('', { nonNullable: true }),
		password: new FormControl<string>('', { nonNullable: true }),
		requestTimeout: new FormControl<number | null>(null, [
			Validators.pattern('^\\d+$'),
			Validators.min(0),
			Validators.max(1000000),
		]),
		email: new FormControl<string>('', { nonNullable: true }),
		customerId: new FormControl<string>('', { nonNullable: true }),
		sourceGt: new FormControl<string>('', { nonNullable: true }),
		destinationGt: new FormControl<string>('', { nonNullable: true }),
		serviceCentreAddress: new FormControl<string>('', { nonNullable: true }),
		verifiedSmsAgentId: new FormControl<string>('', { nonNullable: true }),
		verifiedSmsServiceAccountPath: new FormControl<string>('', { nonNullable: true }),
		verifiedSmsPrivateKeyPath: new FormControl<string>('', { nonNullable: true }),
		domain: new FormControl<string>('', { nonNullable: true }),
		ip: new FormControl<string>('', { nonNullable: true, validators: [NetworkValidators.networkValidator] }),
		port: new FormControl<number | null>(null, [
			Validators.pattern('^\\d+$'),
			Validators.min(1),
			Validators.max(65535),
		]),
		customTranslation: new FormControl<string | null>(null, AdditionalValidators.checkJson()),
		mccmncTranslation: new FormControl<string | null>(null, AdditionalValidators.checkJson()),
		mccmncTranslationBy: new FormControl<string | null>(null, AdditionalValidators.checkJson()),
		mccmncTranslationByStatus: new FormControl<string | null>(null, AdditionalValidators.checkJson({ allowUnderscores: true })),
		mccSet: new FormControl<string | null>(null, AdditionalValidators.checkArrayThreeNumbers),
		errorCodeTranslation: new FormControl<string | null>(null, AdditionalValidators.checkJson()),
		apiKey: new FormControl<string>('', { nonNullable: true }),
		customerKey: new FormControl<string>('', { nonNullable: true }),
		secretKey: new FormControl<string>('', { nonNullable: true }),
		forceErrCodeTranslation: new FormControl<boolean | null>(null),
		retries: new FormControl<number | null>(3, [Validators.pattern('^\\d+$'), Validators.min(0)]),
		forceRerouteKeepingMccmnc: new FormControl<boolean | null>(null),
		forceRerouteKeepingMccmncMode: new FormControl<ForceOption>({
			key: 'DISABLED',
			label: this.forceRerouteMap['DISABLED'],
		}, { nonNullable: true }),
		cacheSuccessfulResponseOnly: new FormControl<number | null>(0),
		responseCodeCacheList: new FormControl<string | null>(null, AdditionalValidators.checkArray),
		useErrorCode: new FormControl<boolean | null>(null),
		useStatusId: new FormControl<boolean | null>(null),
		fixedMccMnc: new FormControl<boolean | null>(null),
		invNumberCode: new FormControl<number | null>(null, Validators.pattern('^\\d+$')),
		hlrResponseCodeFrom: new FormControl<string>('', { nonNullable: true }),
		presenceMccmnc: new FormControl<string>('', { nonNullable: true }),
		senderAddress: new FormControl<string>('', { nonNullable: true, validators: [
				Validators.pattern(AdditionalValidators.ipPattern),
				this.validateSenderAddress.bind(this),] }),
		senderPort: new FormControl<number | null>(null, [
			Validators.pattern('^\\d+$'),
			Validators.min(0),
			Validators.max(65535),
		]),
		ignoreNotPorted: new FormControl<boolean | null>(null),
		ignoreWireline: new FormControl<boolean | null>(null),
		statusRequestDelay: new FormControl<number | null>(1000, [Validators.pattern('^(0|[1-9]\\d*)$')]),
		activeDb: new FormControl<number | null>(null, [Validators.pattern('^\\d+$')]),
		stageDb: new FormControl<number | null>(null, [Validators.pattern('^\\d+$')]),
		doNotUseMccmnc: new FormControl<boolean | null>(null),
		mnpFieldType: new FormControl<string>('', { nonNullable: true }),
		unconditionalUsage: new FormControl<boolean | null>(null),
	});

	currencies = signal<HlrCurrencies[]>([]);
	providers = signal<Provider[]>([]);
	private typeSignal = signal<Provider | null>(null);
	readonly preparedFields = computed(() => {
		this.languageSignal();
		const type = this.typeSignal();
		const groups = this.providersService.map.get(type?.name ?? '')?.groups ?? [];
		const result: (
			| { type: 'header'; groupName: string }
			| { type: 'field'; field: ProviderField; control: FormControl;
			tableField: TableEntityField; example: string; fieldName: string }
			)[] = [];

		for (const group of groups) {
			result.push({
				type: 'header',
				groupName: group.name,
			});
			let index = 0;
			for (const field of group.fields) {
				result.push({
					type: 'field',
					field,
					control: this.getControl(field),
					tableField: this.getField(field, index),
					example: this.getExample(field),
					fieldName: this.toCamelCase(field.fieldName),
				});
				index++;
			}
		}
		return result;
	});

	readonly currencyFilter = new FormControl<string>('');
	readonly providersFilter = new FormControl<string>('');
	readonly tabs = [
		{ label: 'sources.cache_all', value: 0 },
		{ label: 'sources.only_successful', value: 1 },
		{ label: 'sources.only_specific', value: 2 },
	];
	readonly connectionTypeTabs = [
		{ label: 'ENUM', value: ConnectionType.ENUM },
		{ label: 'HTTP', value: ConnectionType.HTTP },
		{ label: 'SS7', value: ConnectionType.SS7 },
		{ label: 'MNP', value: ConnectionType.MNP },
		{ label: 'CAMARA', value: ConnectionType.CAMARA },
		{ label: 'AI', value: ConnectionType.AI },
	];
	mnpFieldTypes: string[] = ['TRASH', 'UNALLOCATED', 'RCS_CAPABLE', 'WA_CAPABLE'];

	init(source: Source): void {
		this.sourceForm.reset({}, { emitEvent: false });

		forkJoin({
			currencies: this.currenciesService.readActive().pipe(take(1)),
			providers: this.providersService.loadProviders(source.connectionType).pipe(take(1))
		}).pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.currencies.set(this.currenciesService.list$.value);
				this.providers.set(this.providersService.list);

				const matchedCurrency = this.currencies().find(c => c.name === source.currencyName) ?? null;
				const provider = this.providers().find(p => p.name === source.type) ?? null;

				const forceKey = source.forceRerouteKeepingMccmncMode ?? 'DISABLED';
				const forceOption: ForceOption = {
					key: forceKey,
					label: this.forceRerouteMap[forceKey],
				};

				this.sourceForm.reset({
					...source,
					type: provider,
					currencyName: matchedCurrency,
					huntStop: {
						key: source.huntStop ?? HuntStop.DISABLED,
						label: this.huntstopMap[source.huntStop ?? HuntStop.DISABLED]
					},
					forceRerouteKeepingMccmncMode: forceOption,
				}, { emitEvent: false });
				this.typeSignal.set(provider);
				this.setupSubscriptions();
				const currentType = this.sourceForm.controls.type.value;
				const currentConnection = this.sourceForm.controls.connectionType.value;
				this.applyValidatorsPerType(
					currentType?.name ?? '',
					currentConnection
				);
				this.sourceForm.markAllAsTouched();
			});
	}

	private setupSubscriptions(): void {
		this.settings.read()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.defaultRequestTimeout = this.settings.settings?.defaultRequestTimeout ?? 1000;
			});

		this.currencyFilter.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(value => {
				const all = this.currenciesService.list$.value;
				this.currencies.set(all.filter(c =>
					c.name.toLowerCase().includes(value?.toLowerCase() ?? '')
				));
			});

		this.currenciesService.getSystemCurrency()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(currency => {
				const currencyControl = this.sourceForm.controls.currencyName;
				const sourceIdControl = this.sourceForm.controls.sourceId;
				if (!currency || currencyControl.value || !sourceIdControl.value) return;
				const matched = this.currencies().find(c => c.name === currency.name) ?? null;
				if (matched) {
					currencyControl.setValue(matched, { emitEvent: false });
				}
			});

		this.sourceForm.controls.connectionType.valueChanges
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				switchMap(connectionType => {
					return this.providersService.loadProviders(connectionType);
				})
			)
			.subscribe(() => {
				this.providers.set(this.providersService.list);
				this.providersFilter.reset();
				this.sourceForm.controls.type.reset();
				this.sourceForm.controls.type.updateValueAndValidity({ emitEvent: false });
				const type = this.sourceForm.controls.type.value;
				this.applyValidatorsPerType(
					type?.name ?? '',
					this.sourceForm.controls.connectionType.value
				);
				this.sourceForm.markAllAsTouched();
			});

		this.providersFilter.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(value => {
				const all = this.providersService.list;
				this.providers.set(all.filter(p =>
					p.name.toLowerCase().includes(value?.toLowerCase() ?? '')
				));
			});

		this.sourceForm.controls.type.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(type => {
				this.typeSignal.set(type);
				if (!type) return;
				const providerMeta = this.providersService.map.get(type.name);
				if (!providerMeta) return;
				const connectionType = providerMeta.type;
				const current = this.sourceForm.getRawValue();

				Object.values(this.sourceForm.controls).forEach(control => control.clearValidators());

				this.sourceForm.patchValue({
					sourceId: current.sourceId ?? null,
					name: current.name ?? '',
					type: type,
					connectionType: connectionType,
					retries: connectionType === ConnectionType.ENUM ? 1 : 3,
				}, { emitEvent: false });

				Object.values(this.sourceForm.controls).forEach(control =>
					control.updateValueAndValidity({ emitEvent: false }));

				this.applyValidatorsPerType(type.name, connectionType);
				this.sourceForm.markAllAsTouched();
			});

		this.sourceForm.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				const type = this.sourceForm.controls.type.value?.name;
				if (type === 'infobip') {
					this.handleInfobipDependencies();
				}
			});

		this.sourceForm.valueChanges
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				debounceTime(300)
			)
			.subscribe(() => {
				const raw = this.sourceForm.getRawValue();
				const draft: Source = {
					...raw,
					huntStop: raw.huntStop.key ?? null,
					type: raw.type?.name ?? '',
					currencyName: raw.currencyName?.name ?? '',
					forceRerouteKeepingMccmncMode: raw.forceRerouteKeepingMccmncMode?.key ?? 'DISABLED',
				};
				this.sourcesService.saveToSession(draft);
			});
	}

	private handleInfobipDependencies(): void {
		const username = this.sourceForm.controls.username.value;
		const password = this.sourceForm.controls.password.value;
		const apiKey = this.sourceForm.controls.apiKey.value;
		const isApiKeyDisabled = !!username || !!password;
		const areUsernameAndPasswordDisabled = !!apiKey;

		if (isApiKeyDisabled) {
			this.sourceForm.controls.apiKey.disable({ onlySelf: true });
		} else {
			this.sourceForm.controls.apiKey.enable({ onlySelf: true });
		}

		if (areUsernameAndPasswordDisabled) {
			this.sourceForm.controls.username.disable({ onlySelf: true });
			this.sourceForm.controls.password.disable({ onlySelf: true });
		} else {
			this.sourceForm.controls.username.enable({ onlySelf: true });
			this.sourceForm.controls.password.enable({ onlySelf: true });
		}

		const applyValidators = (control: FormControl, required: boolean): void => {
			if (required) {
				control.addValidators(Validators.required);
				control.setErrors({ required: true });
			} else {
				control.removeValidators(Validators.required);
				control.updateValueAndValidity();
			}
		};

		const apiKeyRequired = !isApiKeyDisabled && !areUsernameAndPasswordDisabled;
		const usernameRequired = !isApiKeyDisabled && !areUsernameAndPasswordDisabled;
		const passwordRequired = !isApiKeyDisabled && !areUsernameAndPasswordDisabled;

		applyValidators(this.sourceForm.controls.apiKey, apiKeyRequired);
		applyValidators(this.sourceForm.controls.username, usernameRequired);
		applyValidators(this.sourceForm.controls.password, passwordRequired);
	}

	getPayload(): SourcePayload {
		const raw = this.sourceForm.getRawValue();
		return {
			...raw,
			type: raw.type?.name ?? '',
			currencyName: raw.currencyName?.name ?? '',
			huntStop: raw.huntStop.key ?? null,
			forceRerouteKeepingMccmncMode: raw.forceRerouteKeepingMccmncMode?.key ?? 'DISABLED',
		};
	}

	getControl(field: ProviderField): FormControl {
		const controlName = this.toCamelCase(field.fieldName);
		const control = this.sourceForm.get(controlName) as FormControl | null;
		return control || new FormControl();
	}

	getField(field: ProviderField, index: number): TableEntityField {
		const fieldNameCamel = this.toCamelCase(field.fieldName);
		let filterType: FilterType;
		let type: InputType | InputComplexType;
		switch (fieldNameCamel) {
			case 'fieldsAll':
			case 'forceErrCodeTranslation':
			case 'useErrorCode':
			case 'useStatusId':
			case 'fixedMccMnc':
			case 'unconditionalUsage':
			case 'ignoreWireline':
			case 'doNotUseMccmnc':
			case 'forceRerouteKeepingMccmnc':
			case 'ignoreNotPorted':
				filterType = FilterType.EXACT;
				type = InputComplexType.CHECKBOX;
				break;

			case 'forceRerouteKeepingMccmncMode':
				filterType = FilterType.EXACT;
				type = InputComplexType.SELECT;
				break;

			case 'cacheSuccessfulResponseOnly':
			case 'ttl':
			case 'requestTimeout':
			case 'retries':
			case 'invNumberCode':
			case 'senderPort':
			case 'statusRequestDelay':
			case 'activeDb':
			case 'stageDb':
				filterType = FilterType.EXACT;
				type = InputType.NUMBER;
				break;

			default:
				filterType = FilterType.LIKE;
				type = InputType.TEXT;
		}
		return new TableEntityField({
			id: index,
			data: [],
			name: 'sources.fields.' + field.cfgFieldName,
			variable: field.fieldName,
			required: field.mandatory,
			type,
			filterType,
			property: '',
			readonly: false,
		});
	}

	getDefaultSource(): Source {
		return {
			sourceId: null,
			currencyName: null,
			name: '',
			type: '',
			connectionType: ConnectionType.HTTP,
			url: '',
			cacheSuccessfulResponseOnly: 0,
			responseCodeCacheList: null,
			forceRerouteKeepingMccmnc: false,
			forceRerouteKeepingMccmncMode: 'DISABLED',
			useErrorCode: false,
			useStatusId: false,
			fixedMccMnc: false,
			fieldsAll: false,
			ttl: 0,
			statusRequestUrl: '',
			huntStop: null,
			username: '',
			password: '',
			requestTimeout: null,
			email: '',
			customerId: '',
			serviceCentreAddress: '',
			sourceGt: '',
			destinationGt: '',
			verifiedSmsAgentId: '',
			verifiedSmsServiceAccountPath: '',
			verifiedSmsPrivateKeyPath: '',
			domain: '',
			ip: '',
			port: null,
			senderAddress: '',
			senderPort: null,
			customTranslation: null,
			mccmncTranslation: null,
			mccmncTranslationBy: null,
			mccmncTranslationByStatus: null,
			mccSet: null,
			ignoreNotPorted: false,
			ignoreWireline: false,
			invNumberCode: null,
			hlrResponseCodeFrom: '',
			presenceMccmnc: '',
			retries: 3,
			errorCodeTranslation: null,
			apiKey: '',
			customerKey: '',
			secretKey: '',
			forceErrCodeTranslation: false,
			statusRequestDelay: 1000,
			activeDb: null,
			stageDb: null,
			doNotUseMccmnc: false,
			mnpFieldType: '',
			unconditionalUsage: false,
		};
	}

	getExample(field: ProviderField): string {
		const syntax = this.lService.translate('sources.syntax');
		const examples: Record<string, string> = {
			'Translate error code to MCCMNC': syntax + '{"0":"238002", "r1": "999999"}',
			'MCC list to handle IMSI from response': syntax + '[262,302,352,354,467,503,751]',
			'MCCMNC translation': syntax + '{"238001":"238002", "238003": "999999"}',
			'MCCMNC translation map': syntax + '{"724301":18, "724302":54}',
			'MCCMNC translation map by Network': syntax + '{"AZERCELL":"400001","BAKCELL":"400002", "AZERFON":"400004"}',
			'MCCMNC translation by status': syntax + '{"not_active" : "999002", "0": "546545"}',
			'MCCMNC translation map by IMSI': syntax + '{"123":123456, "231":345215}',
			'API key': this.lService.translate('sources.tooltip_credentials'),
			'User email': this.lService.translate('sources.tooltip_credentials'),
			'Customer ID': this.lService.translate('sources.tooltip_credentials'),
			'Customer key (username)': this.lService.translate('sources.tooltip_credentials'),
			'Secret key (password)': this.lService.translate('sources.tooltip_credentials'),
			'Source URL': syntax + 'https://domain/api/method, http://1.2.3.4/api/method',
			'Status URL': syntax + 'https://domain/api/method, http://1.2.3.4/api/method',
			'URL for first request to provider': syntax + 'https://domain/api/method, http://1.2.3.4/api/method',
			'Translate OCN to MCCMNC': syntax + '{"8304":"302002", "8202":"302003"}',
			'Mapping carrier_id from response to MCCMNC':
				syntax + '{"1" : "440050", "2" : "440020", "3" : "440010", "6" : "440011"}',
			'Translate network name to MCCMNC': syntax + '{"Algar Telecom" : "724032", "Claro" : "724005"}',
			'Translate MNP code to MCCMNC': syntax + '{"321" : "724005", "341" : "724002", "301" : "724018"}',
			'Provider response code list to cache': syntax + '[“1”, “SUCCESS”]',
		};
		return examples[field.webDescription] || '';
	}

	getForceRerouteOptions(): ForceOption[] {
		return Object.entries(this.forceRerouteMap).map(([key, label]) => ({
			key: key as ForceRerouteKeepingMccmncMode,
			label: label
		}));
	}

	getHuntstopOptions(): HuntStopOption[] {
		const formValue = this.sourceForm.getRawValue();
		const map = formValue.connectionType === ConnectionType.MNP ? this.mnpHuntstopMap : this.huntstopMap;
		return Object.entries(map).map(([key, translationKey]) => ({
			key: key as HuntStop,
			label: translationKey
		}));
	}

	hasHuntstopField(): boolean {
		return !!this.preparedFields().find((field) => field.type === 'field' && field.fieldName === 'huntStop');
	}

	private applyValidatorsPerType(type: string, connectionType: ConnectionType | string | undefined): void {
		for (const controlName of Object.keys(this.sourceForm.controls)) {
			const control = this.sourceForm.get(controlName);
			if (control) {
				control.clearValidators();
				control.updateValueAndValidity({ emitEvent: false });
			}
		}

		const groups: ProviderFieldGroup[] = this.providersService.map.get(type)?.groups ?? [];

		const validatorsMap: { [key: string]: ValidatorFn[] } = {
			currencyName: [Validators.required],
			errorCodeTranslation: [AdditionalValidators.checkJson()],
			ip: [NetworkValidators.networkValidator],
			invNumberCode: [Validators.pattern('^\\d+$')],
			customTranslation: [AdditionalValidators.checkJson()],
			mccmncTranslation: [AdditionalValidators.checkJson()],
			mccmncTranslationBy: [AdditionalValidators.checkJson()],
			mccmncTranslationByStatus: [AdditionalValidators.checkJson({ allowUnderscores: true })],
			mccSet: [AdditionalValidators.checkArrayThreeNumbers],
			responseCodeCacheList: [AdditionalValidators.checkArray],
			port: [Validators.pattern('^\\d+$'), Validators.min(1), Validators.max(65535)],
			retries: [Validators.pattern('^\\d+$'),
				connectionType === ConnectionType.ENUM ? Validators.min(1) : Validators.min(0)],
			requestTimeout: [Validators.pattern('^\\d+$'), Validators.min(0), Validators.max(1000000)],
			senderAddress: [Validators.pattern(AdditionalValidators.ipPattern), this.validateSenderAddress()],
			senderPort: [Validators.pattern('^\\d+$'), Validators.min(0), Validators.max(65535)],
			statusRequestDelay: [Validators.pattern('^(0|[1-9]\\d*)$')],
			statusRequestUrl: [Validators.pattern(AdditionalValidators.urlPattern)],
			ttl: [Validators.pattern('^\\d+$')],
			url: [Validators.pattern(AdditionalValidators.urlPattern)],
			activeDb: [Validators.pattern('^\\d+$')],
			stageDb: [Validators.pattern('^\\d+$')],
			unconditionalUsage: [],
		};

		const fieldNamesInGroups = new Set<string>();
		groups.forEach(group => {
			group.fields.forEach(field => {
				fieldNamesInGroups.add(this.toCamelCase(field.fieldName));
			});
		});

		const nameControl = this.sourceForm.get('name');
		if (nameControl) {
			nameControl.setValidators(Validators.required);
			nameControl.updateValueAndValidity({ emitEvent: false });
		}

		const typeControl = this.sourceForm.get('type');
		if (typeControl) {
			typeControl.setValidators(Validators.required);
			typeControl.updateValueAndValidity({ emitEvent: false });
		}

		const currencyControl = this.sourceForm.get('currencyName');
		if (currencyControl) {
			if (fieldNamesInGroups.has('currencyName')) {
				currencyControl.setValidators(Validators.required);
			} else {
				currencyControl.clearValidators();
			}
			currencyControl.updateValueAndValidity({ emitEvent: false });
		}

		groups.forEach(group => {
			group.fields.forEach(field => {
				const controlName = this.toCamelCase(field.fieldName);
				const control = this.sourceForm.get(controlName);
				if (control) {
					const validators = validatorsMap[controlName] || [];
					control.setValidators(validators);
					if (field.mandatory) {
						control.addValidators(Validators.required);
					}
					control.updateValueAndValidity({ emitEvent: false });
				} else {
					console.warn(`Field ${field.fieldName} not found.`);
				}
			});
		});

		this.sourceForm.updateValueAndValidity();
	}

	private validateSenderAddress(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.value) return null;
			return this.localAddresses.map.get(control.value) ? { customErr: { value: control.value } } : null;
		};
	}

	private toCamelCase(str: string): string {
		return str.charAt(0).toLowerCase() + str.slice(1);
	}
}